import { PageModeMixin } from '@/main/mixins/pageMode';
import mixins from 'vue-typed-mixins';
import _ from 'lodash';


import { storesMap } from '../store/registry';
import { EditStoreBase } from '../store/@types/edit.d';
import { TABLE_DATA, TABLE_SCHEMA, TABLE_SCHEMA_DATA, ENTITY, TABLE_DATA_EXPORT } from '../store/listEditTypes';
import { PageInfo } from '../store/pageManager';
import { EditableStatus, Row } from '../utils/table';
import { PageMode } from '../utils/pageMode';


export const EditStoreMixin = mixins(PageModeMixin).extend({
	data() {
		return {
			staticStoreUri: null as string | null,
		};
	},
	computed: {
		pageInfo(): PageInfo {
			return _.last(this.$store.state.pageManager.pages as PageInfo[])!;
		},
		storeUri():string {
			return this.staticStoreUri ?? `pages/${this.pageInfo.storeModule}`;
		},
		store(): EditStoreBase {
			return this.$store.state.pages[this.pageInfo.storeModule!];
		},
		dependencies(): string[] {
			return Object.keys(this.store.dependentTables);
		},
		entityId(): number | null {
			return this.pageInfo.id;
		},
		tableListeners(): {} {
			return {
				refresh: this.refreshTable,
				export: this.exportTable,
				'sort-change': this.saveSort,
			};
		},
	},
	created() {
		this.staticStoreUri = `pages/${this.pageInfo.storeModule!}`;
	},
	methods: {
		/**
		 * commit updateField to mutate value of the key property
		 */
		setField(path: string, value: any): void {
			this.$store.commit(`${this.storeUri}/updateField`, { path, value });
		},
		/**
		 * Dispatches the ENTITY action.
		 */
		entity() {
			return this.$store.dispatch(`${this.storeUri}/${ENTITY}`, { id: this.entityId, edit: this.pageMode === PageMode.EDIT });
		},
		/**
		 * Reset edit and localState in store to initial values
		 */
		resetState() {
			const storeInit = storesMap.get(this.pageInfo.name);
			const { edit, localState } = storeInit.state();
			this.setField('edit', _.cloneDeep(edit));
			this.setField('localState', _.cloneDeep(localState));
		},
		/**
		 * Loads schemas and data of dependent tables by dispatching the TABLE_SCHEMA action for
		 * each table.
		 *
		 * @returns array of table schemas.
		 */
		async fetchTableData(tablesToFetch?: string[]): Promise<any> {
			const tables: string[] = tablesToFetch ?? this.dependencies as string[];

			return Promise.all(
				_.map(tables, (tableName) => this.$store.dispatch(`${this.storeUri}/${tableName}_${TABLE_DATA}`)),
			);
		},
		/**
		 * Loads schemas of dependent tables by dispatching the TABLE_SCHEMA action for
		 * each table.
		 *
		 * @returns array of table schemas.
		 */
		async fetchTableSchemas(tablesToFetch?: string[]) {
			const tables: string[] = tablesToFetch ?? this.dependencies as string[];

			return Promise.all(
				_.map(tables, (tableName) => this.$store.dispatch(`${this.storeUri}/${tableName}_${TABLE_SCHEMA}`)),
			);
		},
		/**
		 * Loads data of dependent tables by dispatching the TABLE_DATA action for
		 * each table.
		 *
		 * @param tablesToFetch = Object.values(this.dependencies).
		 *
		 * @returns array of table rows.
		 */
		async fetchTableSchemaData(tablesToFetch?: string[]): Promise<any> {
			const tables: string[] = tablesToFetch ?? this.dependencies as string[];

			return Promise.all(
				_.map(tables, (tableName) => this.$store.dispatch(`${this.storeUri}/${tableName}_${TABLE_SCHEMA_DATA}`)),
			);
		},
		/**
		 * Refresh data on the page
		 */
		async refreshPage() {
			await this.entity();
			await this.fetchTableData();
		},
		updateRow(rowUpdateData: { tableName: string, updateStatus: EditableStatus, rowId: number, row: Row }) {
			this.$store.commit(`${this.storeUri}/updateTableRow`, rowUpdateData);
		},
		/**
		 * save sort on dependent table
		 */
		saveSort(sort: { columnId: any; order: string }, tableName: string) {
			this.$store.commit(`${this.storeUri}/SAVE_SORT`, { data: { ...sort }, tableName });
		},
		/**
		 * Refresh table data by dependent table name
		 */
		async refreshTable(name: string) {
			await this.$store.dispatch(`${this.storeUri}/${name}_${TABLE_DATA}`, { filter: (this as any)[`${name}Filter`] });
		},
		/**
		 * Export table data by dependent table name
		 */
		async exportTable(name: string) {
			await this.$store.dispatch(`${this.storeUri}/${name}_${TABLE_DATA_EXPORT}`);
		},
	},
});
